import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit2, Trash2, Copy, Check, X, Link, ExternalLink, 
  Users, Calendar, ToggleLeft, ToggleRight, Eye, AlertCircle
} from 'lucide-react';
import api from '../../services/api';

const WidgetLiensPartenaires = () => {
  const [liens, setLiens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);
  
  const [formData, setFormData] = useState({
    nom: '',
    code: '',
    description: ''
  });

  const baseUrl = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin;

  useEffect(() => {
    loadLiens();
  }, []);

  const loadLiens = async () => {
    try {
      setLoading(true);
      const res = await api.get('/liens-partenaires');
      setLiens(res.data || []);
    } catch (err) {
      console.error('Erreur chargement liens:', err);
      setError('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.nom || !formData.code) {
      setError('Le nom et le code sont obligatoires');
      return;
    }
    if (formData.code.length !== 3) {
      setError('Le code doit faire exactement 3 caractères');
      return;
    }

    try {
      await api.post('/liens-partenaires', formData);
      await loadLiens();
      setIsAdding(false);
      setFormData({ nom: '', code: '', description: '' });
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdate = async (id) => {
    try {
      await api.put(`/liens-partenaires/${id}`, {
        nom: formData.nom,
        description: formData.description
      });
      await loadLiens();
      setEditingId(null);
      setFormData({ nom: '', code: '', description: '' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la modification');
    }
  };

  const handleToggleActif = async (lien) => {
    try {
      await api.put(`/liens-partenaires/${lien.id}`, { actif: !lien.actif });
      await loadLiens();
    } catch (err) {
      setError('Erreur lors de la modification');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce lien partenaire ?')) return;
    try {
      await api.delete(`/liens-partenaires/${id}`);
      await loadLiens();
    } catch (err) {
      setError('Erreur lors de la suppression');
    }
  };

  const copyLink = (lien) => {
    const fullLink = `${baseUrl}/demande/${lien.lien_unique}`;
    navigator.clipboard.writeText(fullLink);
    setCopied(lien.id);
    setTimeout(() => setCopied(null), 2000);
  };

  const startEdit = (lien) => {
    setEditingId(lien.id);
    setFormData({
      nom: lien.nom,
      code: lien.code,
      description: lien.description || ''
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
            <Link className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Liens Partenaires</h2>
            <p className="text-sm text-gray-500">Créez des liens pour recevoir des demandes externes</p>
          </div>
        </div>
        <button
          onClick={() => { setIsAdding(true); setFormData({ nom: '', code: '', description: '' }); }}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          disabled={isAdding}
        >
          <Plus className="w-4 h-4" />
          Nouveau lien
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          <AlertCircle className="w-4 h-4" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Formulaire d'ajout */}
      {isAdding && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5 space-y-4">
          <h3 className="font-semibold text-indigo-900">Nouveau lien partenaire</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom du partenaire *
              </label>
              <input
                type="text"
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                placeholder="Ex: CARSAT Rhône-Alpes"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Code référence (3 lettres) *
              </label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase().slice(0, 3) })}
                placeholder="CAR"
                maxLength={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 uppercase"
              />
              <p className="text-xs text-gray-500 mt-1">Les dossiers auront une référence {formData.code || 'XXX'}-2026-001</p>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (optionnel)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Notes internes sur ce partenaire..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => { setIsAdding(false); setFormData({ nom: '', code: '', description: '' }); setError(null); }}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Annuler
            </button>
            <button
              onClick={handleCreate}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Créer le lien
            </button>
          </div>
        </div>
      )}

      {/* Liste des liens */}
      <div className="space-y-3">
        {liens.map((lien) => (
          <div
            key={lien.id}
            className={`bg-white border rounded-xl p-4 ${
              !lien.actif ? 'opacity-60 border-gray-200' : 'border-gray-200'
            }`}
          >
            {editingId === lien.id ? (
              // Mode édition
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Nom</label>
                    <input
                      type="text"
                      value={formData.nom}
                      onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Code (non modifiable)</label>
                    <input
                      type="text"
                      value={formData.code}
                      disabled
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setEditingId(null)}
                    className="px-3 py-1.5 text-gray-600 text-sm"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={() => handleUpdate(lien.id)}
                    className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm"
                  >
                    Sauvegarder
                  </button>
                </div>
              </div>
            ) : (
              // Mode affichage
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="px-2 py-1 bg-indigo-100 text-indigo-700 font-mono text-sm rounded font-bold">
                      {lien.code}
                    </span>
                    <h3 className="font-semibold text-gray-900">{lien.nom}</h3>
                    {!lien.actif && (
                      <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded">
                        Inactif
                      </span>
                    )}
                  </div>
                  
                  {lien.description && (
                    <p className="text-sm text-gray-500 mb-2">{lien.description}</p>
                  )}
                  
                  {/* Lien à copier */}
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex-1 px-3 py-2 bg-gray-50 rounded-lg text-sm font-mono text-gray-600 truncate">
                      {baseUrl}/demande/{lien.lien_unique}
                    </div>
                    <button
                      onClick={() => copyLink(lien)}
                      className={`px-3 py-2 rounded-lg text-sm flex items-center gap-1 transition ${
                        copied === lien.id
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {copied === lien.id ? (
                        <>
                          <Check className="w-4 h-4" />
                          Copié !
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          Copier
                        </>
                      )}
                    </button>
                    <a
                      href={`${baseUrl}/demande/${lien.lien_unique}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm hover:bg-gray-200"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                  
                  {/* Stats */}
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      <span>{lien.nombre_dossiers} dossier(s) créé(s)</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>Créé le {new Date(lien.created_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => handleToggleActif(lien)}
                    className={`p-2 rounded-lg transition ${
                      lien.actif
                        ? 'text-green-600 hover:bg-green-50'
                        : 'text-gray-400 hover:bg-gray-100'
                    }`}
                    title={lien.actif ? 'Désactiver' : 'Activer'}
                  >
                    {lien.actif ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => startEdit(lien)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(lien.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        {liens.length === 0 && !isAdding && (
          <div className="text-center py-12 text-gray-500">
            <Link className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Aucun lien partenaire créé</p>
            <p className="text-sm">Créez votre premier lien pour recevoir des demandes externes</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WidgetLiensPartenaires;
